import { IconCheck, IconError } from '@blockchain-com/icons'
import { actions } from 'data'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { injectIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import KYCStep from 'scenes/ExtraKYC/Steps/KYCStep'
import styled from 'styled-components'
import { color } from 'utils'

import { CeruleanButton, LightGrayButton } from '../../components/Button'
import { Node } from '../../data/Auth/ExtraKYCQuestions/types'
import fetchApi from '../../utils/fetchApi'
import { getApiUrl } from '../../utils/urlBuilder'
import { Wrapper } from '../ExtraKYC'
import FinishedStep from '../ExtraKYC/Steps/FinishedStep'
import Container from '../Modal/Container'

const SelfClassification = ({ onComplete }) => {
  const [data, setData] = useState<any>(null)

  const fetchData = async () => {
    // @ts-ignore
    const { response } = await fetchApi({
      auth: true,
      method: 'GET',
      url: getApiUrl(`nabu-gateway/kyc/extra-questions?context=SELF_CLASSIFICATION`)
    })

    setData(response)
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleSubmit = async (nodes) => {
    // @ts-ignore
    const response = await fetchApi({
      auth: true,
      body: { ...data, nodes },
      method: 'PUT',
      url: getApiUrl(`nabu-gateway/kyc/extra-questions?context=SELF_CLASSIFICATION`)
    })

    if (response.ok) {
      fetchData()

      onComplete()
    }
  }

  const [currentStep, setCurrentStep] = useState(0)
  const [previousStep, setPreviousStep] = useState<number | null>(null)
  // inner state, we collect user input in this object
  const [questions, setQuestions] = useState<Array<Node>>([])
  // ids of steps done
  const [doneQuestions, setDoneQuestions] = useState<Array<string>>([])

  useEffect(() => {
    if (data) {
      setQuestions(data.nodes)
    }
  }, [data])

  if (!data || !questions) {
    return null
  }

  const saveQuestion = (question: Node) => {
    // Done this way to preserve array sorting
    const editedQuestions = [...questions]
    const editedIndex = questions.findIndex((q) => q.id === question.id)
    editedQuestions[editedIndex] = question
    setQuestions(editedQuestions)
    return editedQuestions
  }

  const onEditStep = (questionToEdit) => {
    setPreviousStep(currentStep)
    setCurrentStep(questions.indexOf(questionToEdit))
    setDoneQuestions(doneQuestions.filter((id) => id !== questionToEdit.id))
  }

  const onQuestionDone = (question: Node) => {
    const totalSteps = questions.length - 1

    const newQuestions = saveQuestion(question)

    if (currentStep < totalSteps) {
      setDoneQuestions([...doneQuestions, question.id])
      if (previousStep) {
        setCurrentStep(previousStep)
        setPreviousStep(null)
      } else {
        setCurrentStep(currentStep + 1)
      }
      return
    }

    handleSubmit(newQuestions)
  }

  if (!questions.length) return null
  const openQuestion = questions[currentStep]

  return (
    <Wrapper>
      <Container theme='dark' title='Self-Classification Questionnaire'>
        {questions
          .filter((q) => doneQuestions.includes(q.id))
          .map((question) => (
            <FinishedStep
              key={question.id}
              title={question.text}
              onEdit={() => onEditStep(question)}
            />
          ))}
        {openQuestion && (
          // @ts-ignore
          <KYCStep question={openQuestion} key={openQuestion.id} onDone={onQuestionDone} />
        )}
      </Container>
    </Wrapper>
  )
}

const SelfAssessmentIntroduction = ({ data, onStartQuiz }) => {
  const Header = styled.div`
    text-align: center;
  `

  const HeaderDescription = styled.div`
    font-size: 16px;
  `

  const HeaderImage = styled.img`
    filter: invert(80%);
    margin-bottom: 12px;
  `

  const Footer = styled.div`
    text-align: center;

    a {
      text-decoration: none;
    }
  `

  const FooterDescription = styled.div`
    margin-top: 12px;
    margin-bottom: 16px;
  `

  const LearnMoreButton = styled(LightGrayButton)`
    width: 100%;
    transition: all 0.2s ease-in;
    margin-bottom: 8px;
  `

  const StartQuizButton = styled(CeruleanButton)`
    width: 100%;
    transition: all 0.2s ease-in;

    &:disabled {
      background: ${color('blue400')};
    }
  `

  return (
    <Wrapper>
      <Container theme='dark' title={data?.introPage?.header?.title}>
        <Header>
          <HeaderImage src={data?.introPage?.header?.imageUrl} alt='Incognito sign' height='35px' />
          <HeaderDescription>{data?.introPage?.header?.description}</HeaderDescription>
        </Header>

        <Footer>
          {data?.introPage?.footer.map((item) => {
            if (item.type === 'TEXT') {
              return <FooterDescription key={item?.type}>{item?.text}</FooterDescription>
            }

            // TODO: Make this button truly gray
            if (item.type === 'MINIMAL_BUTTON') {
              return (
                <a key={item?.type} href={item?.action?.url} target='_blank' rel='noreferrer'>
                  <LearnMoreButton>{item?.text}</LearnMoreButton>
                </a>
              )
            }

            if (item.type === 'PRIMARY_BUTTON') {
              return (
                <StartQuizButton key={item?.type} onClick={onStartQuiz}>
                  {item?.text}
                </StartQuizButton>
              )
            }

            return null
          })}
        </Footer>
      </Container>
    </Wrapper>
  )
}

const SelfAssessmentContent = ({ data, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [previousStep, setPreviousStep] = useState<number | null>(null)
  const [questions, setQuestions] = useState<Array<Node>>([])
  const [doneQuestions, setDoneQuestions] = useState<Array<string>>([])

  useEffect(() => {
    if (data) {
      setQuestions(data.pages.map((page) => page.nodes[0]))
    }
  }, [data])

  const saveQuestion = (question: Node) => {
    // Done this way to preserve array sorting
    const editedQuestions = [...questions]
    const editedIndex = questions.findIndex((q) => q.id === question.id)
    editedQuestions[editedIndex] = question
    setQuestions(editedQuestions)
    return editedQuestions
  }

  const onEditStep = (questionToEdit) => {
    setPreviousStep(currentStep)
    setCurrentStep(questions.indexOf(questionToEdit))
    setDoneQuestions(doneQuestions.filter((id) => id !== questionToEdit.id))
  }

  const onQuestionDone = (question: Node) => {
    const totalSteps = questions.length - 1

    const newQuestions = saveQuestion(question)

    if (currentStep < totalSteps) {
      setDoneQuestions([...doneQuestions, question.id])
      if (previousStep) {
        setCurrentStep(previousStep)
        setPreviousStep(null)
      } else {
        setCurrentStep(currentStep + 1)
      }
      return
    }

    onSubmit(newQuestions.map((node) => ({ nodes: [node] })))
  }

  if (!questions.length) return null

  const openQuestion = questions[currentStep]

  return (
    <Wrapper>
      <Container theme='dark' title='Self-Classification Questionnaire'>
        {questions
          .filter((q) => doneQuestions.includes(q.id))
          .map((question) => (
            <FinishedStep
              key={question.id}
              title={question.text}
              onEdit={() => onEditStep(question)}
            />
          ))}
        {openQuestion && (
          // @ts-ignore
          <KYCStep question={openQuestion} key={openQuestion.id} onDone={onQuestionDone} />
        )}
      </Container>
    </Wrapper>
  )
}

const SelfAssessmentResult = ({ quizResult }) => {
  const STATUS_ELEMENTS = {
    FAILURE: {
      subtitle: "You couldn't show us that you understood the risks of investing in cryptocurrency",
      title: "You didn't pass"
    },
    RETRY: {
      subtitle: "You couldn't show us that you understood the risks of investing in cryptocurrency",
      title: "You didn't pass"
    },
    RETRY_LATER: {
      subtitle:
        "You answered one or more of the questions incorrectly. You'll have to wait 24 hours before trying again.",
      title: "You didn't pass"
    },
    SUCCESS: {
      subtitle: 'You have a good understanding of crypto and the risks involved.',
      title: 'Congratulations! You aced it!'
    }
  }

  const status = STATUS_ELEMENTS[quizResult.status]

  const Description = styled.div`
    margin-top: 8px;
    margin-bottom: 12px;
    text-align: center;
    font-size: 16px;
  `

  const ContinueButton = styled(CeruleanButton)`
    width: 100%;
    transition: all 0.2s ease-in;
    margin-bottom: 8px;
  `

  const dispatch = useDispatch()

  const onContinue = () => {
    dispatch(actions.app.hideModal('TIER_TWO'))
  }

  return (
    <Wrapper>
      <Container theme='dark' title={status.title}>
        {quizResult.status === 'SUCCESS' ? (
          <IconCheck height='24px' />
        ) : (
          <IconError height='24px' />
        )}

        <Description>{status.subtitle}</Description>

        <ContinueButton onClick={onContinue}>Continue</ContinueButton>
      </Container>
    </Wrapper>
  )
}

const SelfAssessmentDisabled = () => {
  const Description = styled.div`
    margin-top: 8px;
    margin-bottom: 12px;
    text-align: center;
    font-size: 16px;
  `

  const ContinueButton = styled(CeruleanButton)`
    width: 100%;
    transition: all 0.2s ease-in;
    margin-bottom: 8px;
  `

  const dispatch = useDispatch()

  const onContinue = () => {
    dispatch(actions.app.hideModal('TIER_TWO'))
  }

  return (
    <Wrapper>
      <Container theme='dark' title='Quiz is temporarily disabled'>
        <IconError height='24px' />

        <Description>You must wait 24 hours before retrying the quiz again.</Description>

        <ContinueButton onClick={onContinue}>Continue</ContinueButton>
      </Container>
    </Wrapper>
  )
}

const SelfAssessment = () => {
  const dispatch = useDispatch()

  const [data, setData] = useState<any>(null)

  const [startedQuiz, setStartedQuiz] = useState(false)
  const [quizResult, setQuizResult] = useState(null)

  const fetchData = async () => {
    // @ts-ignore
    const { response } = await fetchApi({
      auth: true,
      method: 'GET',
      url: getApiUrl(`nabu-gateway/onboarding/quiz`)
    })

    setData(response)
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleSubmit = async (pages) => {
    // @ts-ignore
    const { response } = await fetchApi({
      auth: true,
      body: { ...data, pages },
      method: 'PUT',
      url: getApiUrl(`nabu-gateway/onboarding/quiz`)
    })

    setQuizResult(response)

    dispatch(actions.auth.ukFrictions.fetch())
  }

  if (data === null) {
    return null
  }

  if (_.isEmpty(data)) {
    return <SelfAssessmentDisabled />
  }

  if (quizResult) {
    return <SelfAssessmentResult quizResult={quizResult} />
  }

  if (!startedQuiz) {
    return <SelfAssessmentIntroduction data={data} onStartQuiz={() => setStartedQuiz(true)} />
  }

  return <SelfAssessmentContent data={data} onSubmit={handleSubmit} />
}

const UKFrictions = ({ onComplete, state }) => {
  if (!state.isUserFinishSelfClassification) {
    return <SelfClassification onComplete={onComplete} />
  }

  return <SelfAssessment />
}

// @ts-ignore
export default injectIntl(UKFrictions)
